import { useState, useEffect } from 'react';
import { adminApi } from '@/lib/api';
import {
  BarChart3, TrendingUp, AlertTriangle, Users, FileText, Target,
  ChevronDown, Activity, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';

const fmt = (v) => Number(v).toLocaleString('fr-FR');
const fmtKpi = (v, format, unit) => {
  if (format === 'percentage') return `${v.toFixed(1)} %`;
  if (format === 'currency') return `${fmt(Math.round(v))} ${unit || '\u20ac'}`;
  return `${v.toFixed(1)}${unit ? ` ${unit}` : ''}`;
};

function RadarChart({ data }) {
  if (!data || data.length < 3) return null;
  const n = data.length;
  const cx = 150, cy = 150, maxR = 95;
  const gridLvl = [25, 50, 75, 100];
  const pt = (i, pct) => {
    const a = (2 * Math.PI / n) * i - Math.PI / 2;
    const r = (pct / 100) * maxR;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const poly = data.map((d, i) => pt(i, d.avg_percentile).join(',')).join(' ');

  return (
    <div className="relative mx-auto" style={{ width: '280px', height: '280px' }}>
      <svg viewBox="0 0 300 300" className="w-full h-full">
        {gridLvl.map(lvl => {
          const pts = data.map((_, i) => {
            const a = (2 * Math.PI / n) * i - Math.PI / 2;
            const r = (lvl / 100) * maxR;
            return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
          }).join(' ');
          return <polygon key={lvl} points={pts} fill="none" stroke="rgba(27,42,74,0.08)" strokeWidth="0.5" />;
        })}
        {data.map((_, i) => {
          const a = (2 * Math.PI / n) * i - Math.PI / 2;
          return <line key={i} x1={cx} y1={cy} x2={cx + maxR * Math.cos(a)} y2={cy + maxR * Math.sin(a)} stroke="rgba(27,42,74,0.06)" strokeWidth="0.5" />;
        })}
        <polygon points={data.map((_, i) => pt(i, 50).join(',')).join(' ')} fill="none" stroke="rgba(27,42,74,0.15)" strokeWidth="1" strokeDasharray="4,3" />
        <polygon points={poly} fill="rgba(184,134,11,0.15)" stroke="#B8860B" strokeWidth="2" />
        {data.map((d, i) => {
          const [x, y] = pt(i, d.avg_percentile);
          return <circle key={i} cx={x} cy={y} r="4" fill="#B8860B" stroke="white" strokeWidth="2" />;
        })}
      </svg>
      {data.map((d, i) => {
        const a = (2 * Math.PI / n) * i - Math.PI / 2;
        const labelR = 46;
        const pctX = 50 + labelR * Math.cos(a);
        const pctY = 50 + labelR * Math.sin(a);
        const isLeft = pctX < 30;
        const isRight = pctX > 70;
        return (
          <div key={i} className="absolute text-[9px] font-semibold text-[#1B2A4A]/70 whitespace-nowrap leading-tight"
            style={{ left: `${pctX}%`, top: `${pctY}%`, transform: `translate(${isLeft ? '-100%' : isRight ? '0%' : '-50%'}, -50%)`, padding: '0 2px' }}>
            {d.label.length > 18 ? d.label.slice(0, 16) + '...' : d.label}
          </div>
        );
      })}
    </div>
  );
}

function ScoreTrend({ trend }) {
  if (!trend || trend.length < 2) return <p className="text-sm text-gray-400 text-center py-8">{"Pas assez de donn\u00e9es pour le graphique"}</p>;
  const scores = trend.map(t => t.avg_score);
  const minS = Math.min(...scores) * 0.9;
  const maxS = Math.max(...scores) * 1.05;
  const range = maxS - minS || 1;
  const w = 440, h = 100, pad = 30;
  const step = (w - pad * 2) / (trend.length - 1);
  const y = (v) => h - pad - ((v - minS) / range) * (h - pad * 2);
  const path = trend.map((t, i) => `${i === 0 ? 'M' : 'L'}${pad + i * step},${y(t.avg_score)}`).join(' ');
  const monthNames = ['Jan', 'F\u00e9v', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Ao\u00fb', 'Sep', 'Oct', 'Nov', 'D\u00e9c'];

  return (
    <svg viewBox={`0 0 ${w} ${h + 10}`} className="w-full" style={{ height: '140px' }}>
      {[0, 25, 50, 75, 100].map(v => {
        const yy = y(minS + (v / 100) * range);
        return <line key={v} x1={pad} y1={yy} x2={w - pad} y2={yy} stroke="rgba(27,42,74,0.06)" strokeWidth="0.5" />;
      })}
      <path d={path} fill="none" stroke="#B8860B" strokeWidth="2.5" strokeLinecap="round" />
      {trend.map((t, i) => {
        const monthIdx = parseInt(t.period.split('-')[1], 10) - 1;
        return (
          <g key={i}>
            <circle cx={pad + i * step} cy={y(t.avg_score)} r="4" fill="#B8860B" stroke="white" strokeWidth="2" />
            <text x={pad + i * step} y={y(t.avg_score) - 10} textAnchor="middle" fontSize="9" fontWeight="bold" fill="#1B2A4A">{t.avg_score}</text>
            <text x={pad + i * step} y={h + 5} textAnchor="middle" fontSize="8" fill="#9CA3AF">
              {monthNames[monthIdx] || t.period.split('-')[1]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectorOpen, setSectorOpen] = useState(false);

  const loadData = (slug) => {
    setLoading(true);
    adminApi.analyticsBi(slug)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadData(''); }, []);

  const handleSectorChange = (slug) => {
    setSectorOpen(false);
    loadData(slug);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
      <div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" />
    </div>
  );

  if (!data || !data.sectors?.length) return (
    <div className="text-center py-20 text-gray-400" data-testid="analytics-empty">Aucun secteur disponible</div>
  );

  const { summary, radar, worst_kpis, rankings, trend, kpi_stats } = data;
  const barCol = (pct) => pct >= 60 ? '#10B981' : pct >= 40 ? '#B8860B' : '#EF4444';

  // Limit radar to max 8 highest-weight KPIs
  const radarDisplay = [...radar].sort((a, b) => {
    const wA = kpi_stats.find(k => k.kpi_id === a.kpi_id)?.weight || 1;
    const wB = kpi_stats.find(k => k.kpi_id === b.kpi_id)?.weight || 1;
    return wB - wA;
  }).slice(0, 8);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header + Sector filter */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">
            Tableau de bord BI
          </h1>
          <p className="text-gray-400 mt-1 text-sm">
            {"Analyse sectorielle approfondie \u2014 "}{data.current_sector?.name || 'Tous secteurs'}
          </p>
        </div>
        <div className="relative" data-testid="bi-sector-selector">
          <button
            onClick={() => setSectorOpen(!sectorOpen)}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-medium text-[#1B2A4A] hover:border-[#B8860B]/40 transition-all shadow-sm"
          >
            <Activity size={14} className="text-[#B8860B]" />
            {data.current_sector?.name || 'S\u00e9lectionner un secteur'}
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${sectorOpen ? 'rotate-180' : ''}`} />
          </button>
          {sectorOpen && (
            <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto min-w-[220px]">
              {data.sectors.map(s => (
                <button key={s.slug} onClick={() => handleSectorChange(s.slug)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F8F9FA] transition-colors ${s.slug === data.current_sector?.slug ? 'bg-[#1B2A4A]/5 font-semibold text-[#1B2A4A]' : 'text-gray-600'}`}>
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="bi-summary-cards">
        {[
          { label: 'Clients actifs', value: summary.clients_with_data, sub: `${summary.total_clients} \u00e9tablissements`, icon: Users, color: '#1B2A4A' },
          { label: 'Score moyen', value: summary.avg_score, sub: '/100', icon: Target, color: '#B8860B' },
          { label: "Co\u00fbt d\u2019inaction", value: `${fmt(summary.total_inaction)} \u20ac`, sub: '/mois total', icon: AlertTriangle, color: '#EF4444' },
          { label: 'Rapports', value: summary.total_reports, sub: "g\u00e9n\u00e9r\u00e9s", icon: FileText, color: '#1B93A4' },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all" data-testid={`bi-card-${c.label.toLowerCase().replace(/[\s'\u2019]+/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <c.icon size={18} style={{ color: c.color }} className="opacity-60" />
              <span className="text-[9px] font-bold text-gray-300 uppercase tracking-wider">{c.label}</span>
            </div>
            <p className="text-2xl font-bold text-[#1B2A4A] font-manrope">{c.value}</p>
            <p className="text-[10px] text-gray-400 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Row: Radar + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar chart */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="bi-radar-chart">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 size={16} className="text-[#B8860B]" />
            <h2 className="font-bold text-[#1B2A4A] font-manrope text-sm">{"Carte radar \u2014 performance moyenne"}</h2>
          </div>
          <p className="text-[10px] text-gray-400 mb-4">{"Percentile moyen de la cohorte par indicateur (50 = m\u00e9diane)"}</p>
          {radarDisplay.length >= 3 ? (
            <>
              <RadarChart data={radarDisplay} />
              <div className="flex justify-center gap-4 mt-2 text-[9px] text-gray-400">
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#B8860B] inline-block" /> Moyenne cohorte</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#1B2A4A]/15 inline-block border-t border-dashed border-[#1B2A4A]/30" /> {"M\u00e9diane (50)"}</span>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400 text-center py-12">{"Pas assez de KPIs pour afficher le radar"}</p>
          )}
        </div>

        {/* Score trend */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="bi-score-trend">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-[#B8860B]" />
            <h2 className="font-bold text-[#1B2A4A] font-manrope text-sm">{"Tendance des scores \u2014 "}{data.current_sector?.name}</h2>
          </div>
          <p className="text-[10px] text-gray-400 mb-4">{"Score moyen par p\u00e9riode de rapport"}</p>
          <ScoreTrend trend={trend} />
        </div>
      </div>

      {/* Worst KPIs analysis */}
      {worst_kpis.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="bi-worst-kpis">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-red-500" />
            <h2 className="font-bold text-[#1B2A4A] font-manrope text-sm">{"Indicateurs critiques \u2014 zones d\u2019am\u00e9lioration"}</h2>
          </div>
          <p className="text-[10px] text-gray-400 mb-5">{"Tri\u00e9s par percentile moyen croissant. Les leviers prioritaires sont les plus impactants."}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {worst_kpis.filter(k => k.avg_percentile < 60).slice(0, 6).map(k => {
              const isPrimary = k.is_primary_lever;
              return (
                <div key={k.kpi_id}
                  className={`rounded-xl border p-5 ${isPrimary ? 'bg-red-50/50 border-red-200' : 'bg-gray-50/50 border-gray-100'}`}
                  data-testid={`bi-worst-kpi-${k.kpi_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-[#1B2A4A]">{k.name}</p>
                        {isPrimary && <span className="text-[8px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full uppercase">Levier</span>}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-0.5">{"Moy. cohorte : percentile "}{k.avg_percentile}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-bold font-manrope" style={{ color: barCol(k.avg_percentile) }}>{k.avg_percentile}</span>
                      <span className="text-[10px] text-gray-400">/100</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                    <div className="h-full rounded-full transition-all" style={{ width: `${k.avg_percentile}%`, backgroundColor: barCol(k.avg_percentile) }} />
                  </div>

                  {/* Worst establishment detail */}
                  {k.worst_establishment && (
                    <div className="flex items-center gap-3 mb-3 text-xs">
                      <div className="flex items-center gap-1 text-gray-400">
                        <ArrowDownRight size={12} className="text-red-500" />
                        <span className="font-medium text-[#1B2A4A]">{k.worst_establishment}</span>
                      </div>
                      <span className="text-gray-300">|</span>
                      <span className="text-gray-500">
                        {fmtKpi(k.worst_value, k.format, k.unit)} <span className="text-gray-300">vs</span>{' '}{fmtKpi(k.median_value, k.format, k.unit)}{" m\u00e9diane"}
                      </span>
                    </div>
                  )}

                  {/* Euro impact */}
                  {k.euro_impact !== 0 && (
                    <div className={`text-xs font-medium mb-2 ${k.euro_impact < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {"Impact : "}{k.euro_detail}
                    </div>
                  )}

                  {/* Action tip */}
                  {k.action_tip && (
                    <div className="bg-[#B8860B]/[0.06] border border-[#B8860B]/10 rounded-lg p-3 mt-2">
                      <p className="text-[9px] font-bold text-[#B8860B] uppercase tracking-wider mb-0.5">{"Action recommand\u00e9e"}</p>
                      <p className="text-[11px] text-gray-600 leading-relaxed">{k.action_tip}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Performance bars */}
      {kpi_stats.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="bi-kpi-bars">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 size={16} className="text-[#B8860B]" />
            <h2 className="font-bold text-[#1B2A4A] font-manrope text-sm">{"Vue d\u00e9taill\u00e9e des KPIs \u2014 "}{data.current_sector?.name}</h2>
          </div>
          <p className="text-[10px] text-gray-400 mb-5">{"Valeurs m\u00e9dianes et dispersion (Q1\u2013Q3) sur l\u2019ensemble de la cohorte"}</p>

          <div className="space-y-3">
            {kpi_stats.map(k => {
              const rd = radar.find(r => r.kpi_id === k.kpi_id);
              const pct = rd?.avg_percentile || 50;
              return (
                <div key={k.kpi_id} className="flex items-center gap-4">
                  <div className="w-40 text-right shrink-0">
                    <p className="text-xs font-medium text-[#1B2A4A] truncate">{k.name}</p>
                    <p className="text-[10px] text-gray-400">
                      {"m\u00e9d. "}{fmtKpi(k.stats.median, k.format, k.unit)}
                    </p>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden relative">
                        <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, pct)}%`, backgroundColor: barCol(pct) }} />
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300" />
                      </div>
                      <span className="w-12 text-xs font-bold text-right" style={{ color: barCol(pct) }}>
                        {Math.round(pct)}
                      </span>
                    </div>
                  </div>
                  <div className="w-24 text-right shrink-0">
                    <span className="text-[10px] text-gray-400">
                      Q1: {fmtKpi(k.stats.q1, k.format, k.unit)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Establishment rankings */}
      {rankings.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="bi-rankings">
          <div className="flex items-center gap-2 mb-1">
            <ArrowUpRight size={16} className="text-[#B8860B]" />
            <h2 className="font-bold text-[#1B2A4A] font-manrope text-sm">{"Classement des \u00e9tablissements \u2014 "}{data.current_sector?.name}</h2>
          </div>
          <p className="text-[10px] text-gray-400 mb-4">{"Score strat\u00e9gique pond\u00e9r\u00e9 et co\u00fbt d\u2019inaction mensuel"}</p>

          <div className="divide-y divide-gray-50">
            {rankings.map((r, idx) => (
              <div key={r.establishment_id} className="flex items-center py-3 gap-4" data-testid={`bi-rank-${idx}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${idx < 3 ? 'bg-[#B8860B]/10 text-[#B8860B]' : 'bg-gray-50 text-gray-400'}`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1B2A4A] truncate">{r.name}</p>
                </div>
                <div className="text-right">
                  <span className={`text-lg font-bold font-manrope ${r.score >= 60 ? 'text-emerald-600' : r.score >= 40 ? 'text-[#B8860B]' : 'text-red-500'}`}>
                    {r.score}
                  </span>
                  <span className="text-xs text-gray-400">/100</span>
                </div>
                {r.inaction_cost > 0 && (
                  <div className="text-right w-32">
                    <span className="text-xs font-medium text-red-500">{`-${fmt(r.inaction_cost)} \u20ac/mois`}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
